import { z } from "zod";
import { findExecutable } from "../utils/executable.js";
import { runGitCommand } from "../utils/run-git-command.js";
import { execCommand } from "../utils/spawn.js";

// Mirrors the architecture of `github-service.ts` but minimal: just enough
// surface area for the read-only Issues tab (list issues + MRs) and the
// "Add new issue" sidebar action. The shipped daemon talks to GitLab through
// the official `glab` CLI exactly the way the GitHub service shells out to
// `gh` — so credential management is delegated to the user's `glab auth login`
// state and (as a fallback) the `GITLAB_TOKEN` env var inherited from the
// daemon's environment. Appostle stores no GitLab tokens itself.

const DEFAULT_GITLAB_CACHE_TTL_MS = 30_000;
const GITLAB_ENV: NodeJS.ProcessEnv = {
  ...process.env,
  GIT_TERMINAL_PROMPT: "0",
};

const GitLabIssueSummarySchema = z.object({
  iid: z.number(),
  title: z.string().catch(""),
  web_url: z.string().catch(""),
  state: z.string().catch(""),
  description: z.string().nullable().catch(null),
  labels: z.array(z.string()).catch([]),
  updated_at: z.string().catch(""),
});

const GitLabMergeRequestSummarySchema = z.object({
  iid: z.number(),
  title: z.string().catch(""),
  web_url: z.string().catch(""),
  state: z.string().catch(""),
  description: z.string().nullable().catch(null),
  source_branch: z.string().nullable().catch(null),
  target_branch: z.string().nullable().catch(null),
  labels: z.array(z.string()).catch([]),
  updated_at: z.string().catch(""),
});

const GitLabCreatedIssueSchema = z.object({
  iid: z.number(),
  title: z.string().catch(""),
  web_url: z.string().catch(""),
  state: z.string().catch(""),
  description: z.string().nullable().catch(null),
  labels: z.array(z.string()).catch([]),
  updated_at: z.string().catch(""),
});

interface CacheEntry {
  value: unknown;
  expiresAt: number;
  cwd: string;
}

interface InFlightCacheEntry {
  cwd: string;
  promise: Promise<unknown>;
}

interface GitLabServiceDependencies {
  runner: GitLabCommandRunner;
  resolveGlabPath: () => Promise<string | null>;
  now: () => number;
}

export interface GitLabCommandRunnerOptions {
  cwd: string;
}

export interface GitLabCommandResult {
  stdout: string;
  stderr: string;
}

export type GitLabCommandRunner = (
  args: string[],
  options: GitLabCommandRunnerOptions,
) => Promise<GitLabCommandResult>;

export interface GitLabIssueSummary {
  iid: number;
  title: string;
  url: string;
  state: string;
  body: string | null;
  labels: string[];
  updatedAt: string;
}

export interface GitLabMergeRequestSummary {
  iid: number;
  title: string;
  url: string;
  state: string;
  body: string | null;
  sourceBranch: string | null;
  targetBranch: string | null;
  labels: string[];
  updatedAt: string;
}

export interface ListGitLabIssuesOptions {
  cwd: string;
  query?: string;
  limit?: number;
}

export interface ListGitLabMergeRequestsOptions {
  cwd: string;
  query?: string;
  limit?: number;
}

export interface CreateGitLabIssueOptions {
  cwd: string;
  title: string;
  body?: string;
  labels?: string[];
}

export interface GitLabService {
  listIssues(options: ListGitLabIssuesOptions): Promise<GitLabIssueSummary[]>;
  listMergeRequests(options: ListGitLabMergeRequestsOptions): Promise<GitLabMergeRequestSummary[]>;
  createIssue(options: CreateGitLabIssueOptions): Promise<GitLabIssueSummary>;
  isAuthenticated(options: { cwd: string }): Promise<boolean>;
  invalidate(options: { cwd: string }): void;
}

export class GitLabCliMissingError extends Error {
  readonly kind = "missing-cli";

  constructor() {
    super("GitLab CLI (glab) is not installed or not in PATH");
    this.name = "GitLabCliMissingError";
  }
}

export class GitLabAuthenticationError extends Error {
  readonly kind = "auth-failure";
  readonly stderr: string;

  constructor(params: { stderr: string }) {
    super("GitLab CLI authentication failed");
    this.name = "GitLabAuthenticationError";
    this.stderr = params.stderr;
  }
}

export class GitLabCommandError extends Error {
  readonly kind = "command-error";
  readonly args: string[];
  readonly cwd: string;
  readonly exitCode: number | null;
  readonly stderr: string;

  constructor(params: { args: string[]; cwd: string; exitCode: number | null; stderr: string }) {
    super(`GitLab CLI command failed: glab ${params.args.join(" ")}`);
    this.name = "GitLabCommandError";
    this.args = [...params.args];
    this.cwd = params.cwd;
    this.exitCode = params.exitCode;
    this.stderr = params.stderr;
  }
}

interface CreateGitLabServiceOptions {
  ttlMs?: number;
  runner?: GitLabCommandRunner;
  resolveGlabPath?: () => Promise<string | null>;
  now?: () => number;
}

interface CommandFailureLike {
  code?: string | number | null;
  stderr?: string | Buffer;
  stdout?: string | Buffer;
  message?: string;
}

export function createGitLabService(options: CreateGitLabServiceOptions = {}): GitLabService {
  const ttlMs = options.ttlMs ?? DEFAULT_GITLAB_CACHE_TTL_MS;
  const deps: GitLabServiceDependencies = {
    runner: options.runner ?? runGlabCommand,
    resolveGlabPath: options.resolveGlabPath ?? resolveGlabPath,
    now: options.now ?? Date.now,
  };
  const cache = new Map<string, CacheEntry>();
  const inFlight = new Map<string, InFlightCacheEntry>();

  async function cached<T>(params: {
    cwd: string;
    method: string;
    args: unknown;
    load: () => Promise<T>;
  }): Promise<T> {
    const key = buildCacheKey({
      cwd: params.cwd,
      method: params.method,
      args: params.args,
    });
    const cachedEntry = cache.get(key);
    const now = deps.now();
    if (cachedEntry && cachedEntry.expiresAt > now) {
      return cachedEntry.value as T;
    }

    const existing = inFlight.get(key);
    if (existing) {
      return existing.promise as Promise<T>;
    }

    const request = params
      .load()
      .then((value) => {
        if (inFlight.get(key)?.promise === request) {
          cache.set(key, {
            value,
            cwd: params.cwd,
            expiresAt: deps.now() + ttlMs,
          });
        }
        return value;
      })
      .finally(() => {
        if (inFlight.get(key)?.promise === request) {
          inFlight.delete(key);
        }
      });
    inFlight.set(key, { cwd: params.cwd, promise: request });
    return request;
  }

  async function run(args: string[], runOptions: GitLabCommandRunnerOptions): Promise<string> {
    const glabPath = await deps.resolveGlabPath();
    if (!glabPath) {
      throw new GitLabCliMissingError();
    }
    try {
      const result = await deps.runner(args, runOptions);
      return result.stdout.trim();
    } catch (error) {
      throw normalizeGitLabCommandError(error, {
        args,
        cwd: runOptions.cwd,
      });
    }
  }

  return {
    listIssues(options) {
      return cached({
        cwd: options.cwd,
        method: "listIssues",
        args: { query: options.query ?? "", limit: options.limit ?? 20 },
        load: async () => {
          // `glab issue list --output json` returns a JSON array. The
          // `--search` flag is supported by recent glab versions; older
          // builds fall back to client-side filtering.
          //
          // `--all` returns BOTH open and closed issues — without it glab
          // defaults to `--state opened` and the client's "Closed" filter
          // chip would never see any candidates to keep.
          const args = [
            "issue",
            "list",
            "--all",
            "--per-page",
            String(options.limit ?? 20),
            "--output",
            "json",
          ];
          const trimmedQuery = options.query?.trim() ?? "";
          if (trimmedQuery.length > 0) {
            args.push("--search", trimmedQuery);
          }
          const stdout = await run(args, { cwd: options.cwd });
          return parseIssueSummaries(stdout);
        },
      });
    },

    listMergeRequests(options) {
      return cached({
        cwd: options.cwd,
        method: "listMergeRequests",
        args: { query: options.query ?? "", limit: options.limit ?? 20 },
        load: async () => {
          const args = [
            "mr",
            "list",
            "--all",
            "--per-page",
            String(options.limit ?? 20),
            "--output",
            "json",
          ];
          const trimmedQuery = options.query?.trim() ?? "";
          if (trimmedQuery.length > 0) {
            args.push("--search", trimmedQuery);
          }
          const stdout = await run(args, { cwd: options.cwd });
          return parseMergeRequestSummaries(stdout);
        },
      });
    },

    async createIssue(options) {
      // `glab issue create -t <title> [-d <body>] [-l <label>] --no-editor`
      // returns the created issue URL (and other text) on stdout. To get the
      // structured iid we re-fetch via `issue view --output json` after
      // creation. Two roundtrips, but it keeps the parsing path identical to
      // the `list` flow and avoids parsing free-form CLI output.
      const args = ["issue", "create", "-t", options.title, "--no-editor"];
      const body = options.body?.trim();
      if (body && body.length > 0) {
        args.push("-d", body);
      }
      for (const label of options.labels ?? []) {
        if (label.trim().length === 0) continue;
        args.push("-l", label);
      }
      const createStdout = await run(args, { cwd: options.cwd });
      const issueUrl = extractIssueUrl(createStdout);
      const iid = issueUrl ? extractIidFromUrl(issueUrl) : null;
      if (iid === null) {
        // Fallback: fetch the most recent issue. Edge case — only triggers
        // when glab's output format changes unexpectedly.
        const recent = await this.listIssues({ cwd: options.cwd, limit: 1 });
        const fallback = recent[0];
        if (!fallback) {
          throw new GitLabCommandError({
            args,
            cwd: options.cwd,
            exitCode: null,
            stderr: "Could not parse created issue iid from glab output",
          });
        }
        // Invalidate any cached lists so the next read returns fresh data.
        this.invalidate({ cwd: options.cwd });
        return fallback;
      }
      const viewStdout = await run(["issue", "view", String(iid), "--output", "json"], {
        cwd: options.cwd,
      });
      const parsed = GitLabCreatedIssueSchema.parse(JSON.parse(viewStdout || "{}"));
      this.invalidate({ cwd: options.cwd });
      // `issueUrl` is non-null here — `iid` was derived from it above and the
      // null branch already returned. Defensive `?? ""` keeps TS happy without
      // an assertion.
      return {
        iid: parsed.iid,
        title: parsed.title,
        url: parsed.web_url || issueUrl || "",
        state: parsed.state,
        body: parsed.description,
        labels: parsed.labels,
        updatedAt: parsed.updated_at,
      };
    },

    isAuthenticated(options) {
      return cached({
        cwd: options.cwd,
        method: "isAuthenticated",
        args: {},
        load: async () => {
          try {
            await run(["auth", "status"], { cwd: options.cwd });
            return true;
          } catch (error) {
            if (error instanceof GitLabAuthenticationError) {
              throw error;
            }
            if (error instanceof GitLabCommandError && isAuthFailureText(error.stderr)) {
              throw new GitLabAuthenticationError({ stderr: error.stderr });
            }
            throw error;
          }
        },
      });
    },

    invalidate(options) {
      // `cwd === "*"` is treated as a wildcard — used by the OAuth sign-in
      // and sign-out handlers, which don't have a workspace context but need
      // to flush every cached "auth status" / "list" so the next call hits
      // glab fresh with the new (or absent) token.
      const matchAll = options.cwd === "*";
      for (const [key, entry] of cache.entries()) {
        if (matchAll || entry.cwd === options.cwd) {
          cache.delete(key);
        }
      }
      for (const [key, entry] of inFlight.entries()) {
        if (matchAll || entry.cwd === options.cwd) {
          inFlight.delete(key);
        }
      }
    },
  };
}

async function resolveGlabPath(): Promise<string | null> {
  return findExecutable("glab");
}

async function runGlabCommand(
  args: string[],
  options: GitLabCommandRunnerOptions,
): Promise<GitLabCommandResult> {
  return execCommand("glab", args, {
    cwd: options.cwd,
    env: GITLAB_ENV,
    maxBuffer: 10 * 1024 * 1024,
  });
}

function buildCacheKey(params: { cwd: string; method: string; args: unknown }): string {
  return `${params.cwd}:${params.method}:${stableStringify(params.args)}`;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(sortJsonValue(value));
}

function sortJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJsonValue);
  }
  if (!value || typeof value !== "object") {
    return value;
  }
  const entries = Object.entries(value).sort(([left], [right]) => left.localeCompare(right));
  const sorted: Record<string, unknown> = {};
  for (const [key, entryValue] of entries) {
    sorted[key] = sortJsonValue(entryValue);
  }
  return sorted;
}

function normalizeGitLabCommandError(
  error: unknown,
  context: { args: string[]; cwd: string },
): Error {
  if (error instanceof GitLabAuthenticationError) {
    return error;
  }
  if (error instanceof GitLabCommandError) {
    if (isAuthFailureText(error.stderr)) {
      return new GitLabAuthenticationError({ stderr: error.stderr });
    }
    return error;
  }
  const failure = toCommandFailureLike(error);
  if (failure.code === "ENOENT") {
    return new GitLabCliMissingError();
  }
  const stderr = bufferOrStringToString(failure.stderr);
  const message = failure.message ?? "";
  if (isAuthFailureText(stderr) || isAuthFailureText(message)) {
    return new GitLabAuthenticationError({ stderr });
  }
  return new GitLabCommandError({
    args: context.args,
    cwd: context.cwd,
    exitCode: typeof failure.code === "number" ? failure.code : null,
    stderr: stderr || message,
  });
}

function toCommandFailureLike(error: unknown): CommandFailureLike {
  if (!error || typeof error !== "object") {
    return { message: String(error) };
  }
  const record = error as Record<string, unknown>;
  return {
    code:
      typeof record.code === "string" || typeof record.code === "number" || record.code === null
        ? record.code
        : undefined,
    stderr:
      typeof record.stderr === "string" || Buffer.isBuffer(record.stderr)
        ? record.stderr
        : undefined,
    stdout:
      typeof record.stdout === "string" || Buffer.isBuffer(record.stdout)
        ? record.stdout
        : undefined,
    message: typeof record.message === "string" ? record.message : undefined,
  };
}

function bufferOrStringToString(value: string | Buffer | undefined): string {
  if (Buffer.isBuffer(value)) {
    return value.toString("utf8");
  }
  return value ?? "";
}

function isAuthFailureText(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    normalized.includes("glab auth login") ||
    normalized.includes("not logged in") ||
    normalized.includes("no token configured") ||
    normalized.includes("authentication failed") ||
    normalized.includes("authentication required") ||
    normalized.includes("invalid_token") ||
    normalized.includes("401 unauthorized") ||
    normalized.includes("http 401")
  );
}

function parseIssueSummaries(stdout: string): GitLabIssueSummary[] {
  const parsed = z.array(GitLabIssueSummarySchema).parse(JSON.parse(stdout || "[]"));
  return parsed.map((item) => ({
    iid: item.iid,
    title: item.title,
    url: item.web_url,
    state: item.state,
    body: item.description,
    labels: item.labels,
    updatedAt: item.updated_at,
  }));
}

function parseMergeRequestSummaries(stdout: string): GitLabMergeRequestSummary[] {
  const parsed = z.array(GitLabMergeRequestSummarySchema).parse(JSON.parse(stdout || "[]"));
  return parsed.map((item) => ({
    iid: item.iid,
    title: item.title,
    url: item.web_url,
    state: item.state,
    body: item.description,
    sourceBranch: item.source_branch,
    targetBranch: item.target_branch,
    labels: item.labels,
    updatedAt: item.updated_at,
  }));
}

function extractIssueUrl(stdout: string): string | null {
  const match = stdout.match(/https?:\/\/\S+\/-\/issues\/\d+/);
  return match ? match[0] : null;
}

function extractIidFromUrl(url: string): number | null {
  const match = url.match(/\/-\/issues\/(\d+)/);
  if (!match) return null;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export async function resolveGitLabRepo(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["config", "--get", "remote.origin.url"], {
      cwd,
      env: { ...process.env, GIT_OPTIONAL_LOCKS: "0" },
    });
    return parseGitLabRepoFromRemote(stdout.trim());
  } catch {
    return null;
  }
}

function parseGitLabRepoFromRemote(url: string): string | null {
  if (!url) return null;
  // Match common GitLab remote shapes: SSH (`git@gitlab.com:owner/repo.git`),
  // HTTPS (`https://gitlab.com/owner/repo.git`), and self-hosted variants
  // (any host containing `gitlab`).
  let cleaned = url;
  const sshMatch = cleaned.match(/^git@([^:]+):(.+)$/);
  if (sshMatch && /gitlab/i.test(sshMatch[1])) {
    cleaned = sshMatch[2];
  } else {
    const httpsMatch = cleaned.match(/^https?:\/\/([^/]+)\/(.+)$/);
    if (httpsMatch && /gitlab/i.test(httpsMatch[1])) {
      cleaned = httpsMatch[2];
    } else {
      return null;
    }
  }
  if (cleaned.endsWith(".git")) {
    cleaned = cleaned.slice(0, -".git".length);
  }
  return cleaned.includes("/") ? cleaned : null;
}
